import { useCallback, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  selectAIConversations,
  selectAwaitingResponse,
  selectCurrentStreamingMessage,
  selectIsLoading,
  selectMessages,
  setActiveConversation,
  updateMessages,
} from 'slices/aiConversations'
import type { Message } from 'slices/aiConversations/types'

export interface UseAIChatMessagesReturn {
  messages: Message[]
  currentStreamingMessage: Message | null
  isLoading: boolean
  awaitingResponse: boolean
  sequenceCounter: React.MutableRefObject<number>
  messageIdSet: React.MutableRefObject<Set<string>>
  addMessageIds: (message: Message) => void
  removeToolCallMessages: () => void
  addMessageWithOrdering: (newMessage: Message) => void
  setMessagesWithSync: (messages: Message[]) => void
}

/**
 * Hook for accessing and managing chat messages.
 *
 * Handles:
 * - Message list access from Redux
 * - Streaming message state
 * - Loading and awaiting states
 * - Message ordering and deduplication
 * - Message list updates
 */
export const useAIChatMessages = (): UseAIChatMessagesReturn => {
  const dispatch = useDispatch()
  const aiConversations = useSelector(selectAIConversations)

  const messages = useSelector(selectMessages)
  const currentStreamingMessage = useSelector(selectCurrentStreamingMessage)
  const awaitingResponse = useSelector(selectAwaitingResponse)
  const isLoading = useSelector(selectIsLoading)

  const MAX_MESSAGES = 500
  const CLEANUP_TRIGGER = 600

  // Track sequence numbers of messages for ordering
  const sequenceCounter = useRef(0)
  const messageIdSet = useRef(new Set<string>())

  // Store current messages in a ref to avoid stale closures
  const currentMessagesRef = useRef(messages)
  currentMessagesRef.current = messages

  const addMessageIds = useCallback((message: Message) => {
    if (message.id) {
      messageIdSet.current.add(message.id)
    }
    if (message.serverId) {
      messageIdSet.current.add(message.serverId)
    }
  }, [])

  // Initialize active conversation if it doesn't exist
  useEffect(() => {
    if (!aiConversations.activeConversation) {
      dispatch(setActiveConversation({ messages: [] }))
    } else if (aiConversations.activeConversation.messages) {
      // Sync message ID set with existing messages
      messageIdSet.current.clear()
      aiConversations.activeConversation.messages.forEach(msg => {
        addMessageIds(msg)
      })
    }
  }, [dispatch, aiConversations.activeConversation, addMessageIds])

  const removeToolCallMessages = useCallback(() => {
    const currentMessages = currentMessagesRef.current

    // Collect IDs of messages being removed
    const removedIds = currentMessages
      .filter(msg => msg.isToolCall)
      .flatMap(msg => [msg.id, msg.serverId].filter(Boolean) as string[])

    // Remove collected IDs from the dedupe set
    removedIds.forEach(id => {
      messageIdSet.current.delete(id)
    })

    // Filter out tool call messages and dispatch directly to Redux
    const filtered = currentMessages.filter(msg => !msg.isToolCall)

    dispatch(updateMessages(filtered))
  }, [dispatch])

  const addMessageWithOrdering = useCallback(
    (newMessage: Message) => {
      const currentMessages = currentMessagesRef.current

      // Skip if duplicate
      if (
        (newMessage.id && messageIdSet.current.has(newMessage.id)) ||
        (newMessage.serverId && messageIdSet.current.has(newMessage.serverId))
      ) {
        return
      }

      // For user messages, assign new sequence number; for others, use existing
      const normalized =
        newMessage.type === 'user'
          ? { ...newMessage, sequenceNumber: ++sequenceCounter.current }
          : newMessage

      // Update sequence counter to match incoming message sequence numbers to prevent ordering issues
      sequenceCounter.current = Math.max(
        sequenceCounter.current,
        normalized.sequenceNumber ?? sequenceCounter.current
      )

      // Add to message ID set
      addMessageIds(normalized)

      // Add to messages list
      const updatedMessages = [...currentMessages, normalized]

      // Stable, deterministic ordering with multiple fallbacks to prevent nondeterministic sorting
      const sorted = updatedMessages.sort((a, b) => {
        // Primary sort: by sequence number (ensures chronological message order)
        const sequenceDiff = a.sequenceNumber - b.sequenceNumber
        if (sequenceDiff !== 0) return sequenceDiff

        // Secondary sort: by timestamp (fallback for messages with same sequence number)
        const timestampDiff = a.timestamp.getTime() - b.timestamp.getTime()
        if (timestampDiff !== 0) return timestampDiff

        // Tertiary sort: user messages before AI messages (ensures user input appears before response)
        if (a.type !== b.type) return a.type === 'user' ? -1 : 1

        // Final fallback
        return a.id.localeCompare(b.id)
      })

      // Cleanup if needed
      let finalMessages = sorted
      if (sorted.length > CLEANUP_TRIGGER) {
        finalMessages = sorted.slice(-MAX_MESSAGES)

        // Update messageIdSet to remove cleaned up message IDs
        messageIdSet.current.clear()
        finalMessages.forEach(msg => {
          addMessageIds(msg)
        })
      }

      dispatch(updateMessages(finalMessages))
    },
    [dispatch, addMessageIds]
  )

  const setMessagesWithSync = useCallback(
    (newMessages: Message[]) => {
      // Dispatch directly to Redux
      dispatch(updateMessages(newMessages))

      // Update sequence counter to highest existing sequence number to prevent ordering issues
      const highestSequenceNumber =
        newMessages.length > 0
          ? Math.max(...newMessages.map(msg => msg.sequenceNumber))
          : 0
      sequenceCounter.current = highestSequenceNumber

      // Clear and repopulate deduplication set
      messageIdSet.current.clear()
      newMessages.forEach(addMessageIds)
    },
    [dispatch, addMessageIds]
  )

  return {
    messages,
    currentStreamingMessage,
    isLoading,
    awaitingResponse,
    sequenceCounter,
    messageIdSet,
    addMessageIds,
    removeToolCallMessages,
    addMessageWithOrdering,
    setMessagesWithSync,
  }
}
